<template>
  <div>
    <player-breadcrumb
      class="mb-2"
      :view="$t('player_profile.medical')"
      :player="player"
    >
      <v-row no-gutters>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            text
            class="text-none"
            @click="playerMedicalStats.filtersDialog = true"
          >
            {{ $t('button.filters') }}
          </v-btn>
        </v-col>
      </v-row>
    </player-breadcrumb>
    <v-row
      class="my-2"
    >
      <v-col>
        <stat-card
          :title="$t('card_title.no_of_injuries_last_year')"
          :value="player.data.injury_stats ? player.data.injury_stats.injuries_count : 0"
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.training_missed_last_year')"
          :value="player.data.injury_stats ? player.data.injury_stats.events_missed_count : 0"
          visual
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.avg_recovery_time')"
          :value="playerMedicalStats.data.avg_recovery_time_in_days ? playerMedicalStats.data.avg_recovery_time_in_days : 0"
          :value-text="playerMedicalStats.data.avg_recovery_time_in_days ? (playerMedicalStats.data.avg_recovery_time_in_days + ' ' + $t('repeat_settings.day.plural')) : 'n/a'"
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.total_recovery_time')"
          :value="playerMedicalStats.data.total_recovery_time_in_days ? playerMedicalStats.data.total_recovery_time_in_days : 0"
          :value-text="playerMedicalStats.data.total_recovery_time_in_days ? (playerMedicalStats.data.total_recovery_time_in_days + ' ' + $t('repeat_settings.day.plural')) : 'n/a'"
        />
      </v-col>
      <v-col>
        <stat-card
          visual
          :max="100"
          :title="$t('card_title.player_availability')"
          :value="playerMedicalStats.data.availability_percentage ? playerMedicalStats.data.availability_percentage : 0"
          :value-text="playerMedicalStats.data.availability_percentage ? (playerMedicalStats.data.availability_percentage + '%') : 'n/a'"
        />
      </v-col>
    </v-row>
    <div class="mb-10">
      <v-data-table
        :headers="tableHeaders"
        :items="injuries.data"
        hide-default-footer
        :loading="injuries.loading"
        :items-per-page="injuries.pagination.per_page"
        disable-sort
      >
        <template
          v-slot:item="{ item: injury }"
        >
          <tr
            style="cursor: pointer"
            @click="selectedInjury.injury = injury; selectedInjury.dialog = true"
          >
            <td>
              {{ injury.title }}
            </td>
            <td style="width: 20%">
              {{ $dayjs(injury.date).format('ddd, ll') }}
            </td>
            <td>
              <div v-if="injury.event">
                {{ injury.event.title }}
              </div>
              <div
                v-else
                class="grey--text"
              >
                N/A
              </div>
            </td>
            <td style="width: 20%">
              <span v-if="injury.completed_date">
                {{ $dayjs(injury.completed_date).format('ddd, ll') }}
              </span>
              <span
                v-else
                class="grey--text"
              >
                N/A
              </span>
            </td>
            <td style="width: 20%">
              <v-chip
                v-if="injury.completed_date"
                label
                small
                dark
                color="success"
              >
                {{ $t('status.injury_status.recovered') }}
              </v-chip>
              <v-chip
                v-else
                label
                small
                dark
                :color="injuryPhases[injury.injury_phases[injury.injury_phases.length - 1].phase].color"
              >
                {{ injuryPhases[injury.injury_phases[injury.injury_phases.length - 1].phase].text }}
              </v-chip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <div
        v-if="injuries.pagination && injuries.pagination.last_page > 1"
        class="d-flex justify-center align-center py-5"
      >
        <pagination
          v-model="injuries.pagination.current_page"
          :last-page="injuries.pagination.last_page"
          @input="fetchInjuries()"
        />
      </div>
    </div>

    <section
      v-if="!playerMedicalStats.loading"
      class="mb-12"
    >
      <v-row>
        <v-col style="min-width: 0">
          <line-chart
            v-if="injuryCharacterStats"
            :raw-data="injuryCharacterStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="character"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryCharacterStats.map(s => s.character),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_character') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_character') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col style="min-width: 0">
          <line-chart
            v-if="injuryContactStats"
            :raw-data="injuryContactStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="contact"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryContactStats.map(s => s.contact),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_contact') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_contact') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          style="flex: 0 0 500px"
        >
          <v-card class="pa-0 rounded-lg fill-height">
            <v-card-title class="pa-6">
              <v-row
                dense
                align="center"
              >
                <v-col class="text-no-wrap">
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('medical.injury_locations') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-btn-toggle
                    v-model="injuryLocationsView"
                    color="primary"
                    mandatory
                  >
                    <v-btn
                      small
                      class="text-none"
                      value="visual"
                    >
                      {{ $t('card.display_option.visual') }}
                    </v-btn>
                    <v-btn
                      small
                      class="text-none"
                      value="list"
                    >
                      {{ $t('card.display_option.list') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pt-6">
              <div v-if="injuryLocationsView === 'visual'">
                <v-row justify="center">
                  <v-col cols="auto">
                    <human-body-front-muscles
                      v-if="player.data.gender === 'male'"
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="playerMedicalStats.data.injury_main_body_part_counts"
                    />
                    <human-body-female-front-muscles
                      v-else
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="playerMedicalStats.data.injury_main_body_part_counts"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <human-body-back-muscles
                      v-if="player.data.gender === 'male'"
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="playerMedicalStats.data.injury_main_body_part_counts"
                    />
                    <human-body-female-back-muscles
                      v-else
                      class="mx-3"
                      style="height: 290px; width: 170px"
                      type="average"
                      :stats="playerMedicalStats.data.injury_main_body_part_counts"
                    />
                  </v-col>
                </v-row>
              </div>
              <div v-else>
                <v-simple-table
                  dense
                  fixed-header
                  height="330px"
                >
                  <thead>
                    <tr>
                      <th>{{ $t('medical.body_location') }}</th>
                      <th>{{ $t('medical.count_of_injuries') }}</th>
                    </tr>
                  </thead>
                  <tbody v-if="playerMedicalStats.data.injury_body_part_counts && playerMedicalStats.data.injury_body_part_counts.length === 0">
                    <tr>
                      <td
                        colspan="2"
                        class="text-center"
                      >
                        {{ $t('data_empty') }}
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr
                      v-for="(bodyPart, index) in playerMedicalStats.data.injury_body_part_counts"
                      :key="bodyPart.label + index"
                    >
                      <td>
                        {{ bodyPart.label }}
                      </td>
                      <td
                        style="width: 15%"
                      >
                        {{ bodyPart.value }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <line-chart
            v-if="injuryMechanismStats"
            :raw-data="injuryMechanismStats"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="mechanism"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: injuryMechanismStats.map(s => s.mechanism),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_mechanism') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_mechanism') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col style="min-width: 0">
          <line-chart
            v-if="playerMedicalStats.data.injury_type_counts && playerMedicalStats.data.injury_type_counts.length > 0"
            :raw-data="playerMedicalStats.data.injury_type_counts"
            :chart-value-keys="[ { key: 'value', type: 'bar', label: $t('label.count') } ]"
            x-value="label"
            y-value-prefix=""
            :xaxis="{
              labels: {
                rotate: -45
              },
              categories: playerMedicalStats.data.injury_type_counts.map(s => $t(`injuryType.${toCamelCase(s.label)}`)),
              tickPlacement: 'between'
            }"
            :yaxis="[ {
              min: 0,
              labels: {
                formatter: function (val) {
                  return val.toFixed(0)
                }
              }
            } ]"
          >
            <template v-slot:header>
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('medical.injuries_type') }}
                </div>
              </v-card-title>
            </template>
          </line-chart>
          <v-card
            v-else
            class="fill-height"
          >
            <v-card-title class="pa-6">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('medical.injuries_type') }}
              </div>
            </v-card-title>
            <v-card-text class="grey--text text-center py-16">
              {{ $t('data_empty') }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <player-injury-dialog
      v-if="selectedInjury.injury"
      :player="player.data"
      :injury-id="selectedInjury.injury.id"
      :visible.sync="selectedInjury.dialog"
      @update:injuries="fetchInjuries()"
    />

    <medical-filter-dialog
      :visible.sync="playerMedicalStats.filtersDialog"
      :filters.sync="playerMedicalStats.filters"
      :player="player"
      @click:apply="fetchInjuryStats()"
    />
  </div>
</template>

<script>
import StatCard from '@/components/StatCard.vue'
import Pagination from '@/components/Pagination.vue'
import { InjuryApiService } from '@/data/services/InjuryApiService'
import { CharacterList, ContactList, InjuryPhases, MechanismList } from '@/data/models/Injury'
import PlayerInjuryDialog from '@/components/players/PlayerInjuryDialog.vue'
import i18n from '@/plugins/i18n'
import { EventBus } from '@/plugins/bus'
import LineChart from '@/components/charts/LineChart.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import { toCamelCase } from '@/utils/helpers'
import MedicalFilterDialog from '@/components/dialogs/MedicalFilterDialog.vue'
import { PlayerApiService } from '@/data/services'
import PlayerBreadcrumb from '@/components/players/PlayerBreadcrumb.vue'
import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'

export default {
  name: 'PlayerMedicalView',
  components: {
    HumanBodyFemaleBackMuscles,
    HumanBodyFemaleFrontMuscles,
    PlayerBreadcrumb,
    MedicalFilterDialog,
    HumanBodyBackMuscles,
    HumanBodyFrontMuscles,
    LineChart,
    PlayerInjuryDialog,
    Pagination,
    StatCard
  },
  props: {
  },
  data() {
    return {
      injuries: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 25
        }
      },
      selectedInjury: {
        injury: undefined,
        dialog: false
      },
      playerMedicalStats: {
        loading: false,
        data: {},
        filtersDialog: false,
        filters: {
          injuryState: undefined,
          eventTypes: [],
          dateRange: [],
          eventIds: [],
          injuryBodyPartIds: [],
          injuryTypeIds: [],
          characters: [],
          contacts: [],
          mechanisms: [],
          injurySurfaceIds: [],
          injuryPhases: [],
          recurrent: undefined,
          completed: undefined
        }
      },
      player: {
        loading: false,
        data: {}
      },
      injuryLocationsView: 'visual'
    }
  },
  computed: {
    injuryCharacterStats() {
      if (this.playerMedicalStats.data.length === 0) return null
      if (this.playerMedicalStats.data.injury_character_counts && Object.keys(this.playerMedicalStats.data.injury_character_counts).length === 0) return null

      return Object.keys(this.character).map(key => {
        return {
          character: this.character[key].text,
          value: this.playerMedicalStats.data.injury_character_counts && (this.playerMedicalStats.data.injury_character_counts[key] || 0)
        }
      })
    },
    injuryContactStats() {
      if (this.playerMedicalStats.data.length === 0) return null
      if (this.playerMedicalStats.data.injury_contact_counts && Object.keys(this.playerMedicalStats.data.injury_contact_counts).length === 0) return null

      return [
        {
          contact: this.$t('contact.no_contact'),
          value: this.playerMedicalStats.data.injury_contact_counts && (this.playerMedicalStats.data.injury_contact_counts.no_contact || 0)
        },
        ...Object.keys(this.contact).map(key => {
          return {
            contact: this.contact[key].text,
            value: this.playerMedicalStats.data.injury_contact_counts && (this.playerMedicalStats.data.injury_contact_counts[key] || 0)
          }
        })
      ]
    },
    injuryMechanismStats() {
      if (this.playerMedicalStats.data.length === 0) return null
      if (this.playerMedicalStats.data.injury_mechanism_counts && Object.keys(this.playerMedicalStats.data.injury_mechanism_counts).length === 0) return null

      return Object.keys(this.mechanism).map(key => {
        return {
          mechanism: this.mechanism[key].text,
          value: this.playerMedicalStats.data.injury_mechanism_counts && (this.playerMedicalStats.data.injury_mechanism_counts[key] || 0)
        }
      })
    }
  },
  created() {
    this.injuryPhases = InjuryPhases
    this.character = CharacterList
    this.contact = ContactList
    this.mechanism = MechanismList

    this.playerMedicalStats.filters.dateRange = [this.$dayjs().format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]

    this.tableHeaders = [
      {
        value: 'id',
        text: i18n.t('table_headers.medical.latest_injuries.injury'),
        sortable: false
      },
      {
        value: 'date',
        text: i18n.t('table_headers.individual_medical.date_started')
      },
      {
        value: 'event_id',
        text: i18n.t('table_headers.individual_medical.event')
      },
      {
        value: 'completed_date',
        text: i18n.t('table_headers.individual_medical.date_recovered')
      },
      {
        value: 'status',
        text: i18n.t('table_headers.player_overview.status')
      }
    ]

    this.fetchPlayerDetails()
    this.fetchInjuries()

    EventBus.$on(this.$events.INJURY_CREATED, () => {
      this.fetchInjuries()
    })

    EventBus.$on(this.$events.INJURY_UPDATED, () => {
      this.fetchInjuries()
    })

    this.fetchInjuryStats()
  },
  methods: {
    toCamelCase,
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
      }
    },
    async fetchInjuries() {
      try {
        this.injuries.loading = true

        const queryParams = {
          page: this.injuries.pagination.current_page,
          per_page: this.injuries.pagination.per_page,
          player_id: this.$route.params.id,
          order_by: 'date',
          order: 'DESC'
        }

        const resp = await InjuryApiService.fetchInjuries(queryParams)

        this.injuries.data = resp.data
        this.injuries.pagination = resp.meta
      } catch (e) {
        this.injuries.data = []
        this.injuries.pagination.current_page = 1
        this.injuries.pagination.last_page = 1
      } finally {
        this.injuries.loading = false
      }
    },
    async fetchInjuryStats() {
      this.playerMedicalStats.loading = true

      try {
        const queryParams = {
          date_from: this.playerMedicalStats.filters.dateRange[0],
          date_to: this.playerMedicalStats.filters.dateRange[1],
          event_ids: this.playerMedicalStats.filters.eventIds,
          injury_body_part_ids: this.playerMedicalStats.filters.injuryBodyPartIds,
          injury_type_ids: this.playerMedicalStats.filters.injuryTypeIds,
          characters: this.playerMedicalStats.filters.characters,
          contacts: this.playerMedicalStats.filters.contacts,
          mechanisms: this.playerMedicalStats.filters.mechanisms,
          injury_surface_ids: this.playerMedicalStats.filters.injurySurfaceIds,
          injury_phases: this.playerMedicalStats.filters.injuryPhases
        }

        this.playerMedicalStats.data = await InjuryApiService.fetchPlayerStats(this.$route.params.id, queryParams)
      } catch (e) {
        this.playerMedicalStats.data = []
      } finally {
        this.playerMedicalStats.loading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
